import { Component, Input, OnInit } from '@angular/core';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { Workflow } from '@models/Sessions/Workflow';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '@services/config.service';
import { MoveIdeasService } from '@services/sessions/move-ideas.service';
import { SessionWorkflowService } from '@services/sessions/session.workflow.service';
import { MessageService } from 'primeng/api';
import { forkJoin, take } from 'rxjs';

@Component({
  selector: 'app-move-ideas-modal',
  templateUrl: './move-ideas-modal.component.html',
  styleUrls: ['./move-ideas-modal.component.scss'],
})
export class MoveIdeasModalComponent implements OnInit {
  @Input() session: Session;
  @Input() state: 'pick-ideas' | 'pick-session' = 'pick-ideas';
  @Input() target: 'workflow' | 'all' = 'workflow';
  @Input() pickedIdeas: Idea[] = [];
  @Input() pickedSession: Session;

  public workflow: Workflow;
  public potentialTargets: Session[];
  public moving = false;

  constructor(
    public activeModal: NgbActiveModal,
    public service: MoveIdeasService,
    private workflowService: SessionWorkflowService,
    public config: ConfigService,
    private messageService: MessageService, // Assuming you have a message service for alerts
  ) {}

  ngOnInit() {
    this.service.potentialTargetSessions(this.session).subscribe(sessions => {
      this.potentialTargets = sessions ?? [];
    });

    this.workflowService.load().subscribe(workflow => {
      this.workflow = workflow;
    });

    // Check if the Topic has any workflow edges to other Topics on load
    forkJoin({
      sessions: this.service
        .potentialTargetSessions(this.session)
        .pipe(take(1)),
      workflow: this.workflowService.load().pipe(take(1)),
    }).subscribe(
      ({ sessions, workflow }: { sessions: Session[]; workflow: Workflow }) => {
        if (!workflow) return;
        if (!sessions || sessions.length === 0) return;

        const result = sessions.filter(session =>
          workflow.edges.some(
            edge =>
              edge.from.toString() === this.session._id.toString() &&
              edge.to.toString() === session._id.toString(),
          ),
        );

        // If there are no workflow edges (other Topics) for the Topic, set target to all
        if (result.length === 0) {
          this.target = 'all';
        }
      },
    );
  }

  get targetSessions() {
    if (this.target === 'workflow') {
      if (this.workflow) {
        return (this.potentialTargets ?? []).filter(session =>
          this.workflow.edges.some(
            e =>
              e.from.toString() === this.session._id.toString() &&
              e.to.toString() === session._id.toString(),
          ),
        );
      } else {
        return [];
      }
    } else {
      return this.potentialTargets;
    }
  }

  dismiss() {
    this.activeModal.dismiss();
  }

  moveIdeas() {
    this.moving = true;
    this.workflowService
      .moveIdeas(this.session, this.pickedIdeas, this.pickedSession)
      .subscribe(() => {
        this.dismiss();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Projects were successfully moved.',
        });
      });
  }

  pickSession() {
    this.state = 'pick-session';
  }

  pickIdeas() {
    this.state = 'pick-ideas';
  }
}
